// src/config/appConfig.ts
export type AppConfig = {
    API_BASE_URL: string;
    COMPANY_NAME: string;
};

// 빈 문자열은 무시
const orUndef = (v: any) =>
    v === undefined || v === null || (typeof v === 'string' && v.trim() === '')
        ? undefined
        : v;

// 호스트 분류: dotech.* 우선
function detectHost() {
    const host = (window.location.hostname || '').toLowerCase();
    const isDotech =
        host.includes('dotech.') || host === 'dotech.work' || host === 'dotech.seojin.work';
    const isLocal = host === 'localhost' || host === '127.0.0.1';
    return { host, isDotech, isLocal };
}

// 환경/윈도/호스트에서 읽기 (회사명은 임시 값, 마지막에 강제 보정)
function resolveEnv(): Partial<AppConfig> {
    const fromWindow = (window as any).__APP_CONFIG__ as Partial<AppConfig> | undefined;
    const viteEnv =
        (typeof import.meta !== 'undefined' ? (import.meta as any).env : undefined) || {};

    const { host, isDotech, isLocal } = detectHost();

    // API: window > vite > CRA > host 매핑
    const apiFromWindow = orUndef(fromWindow?.API_BASE_URL);
    const apiFromVite = orUndef((viteEnv as any)?.VITE_API_BASE_URL);
    const apiFromCRA = orUndef(process.env.REACT_APP_API_BASE_URL);

    let apiFromHost: string | undefined;
    if (isDotech) apiFromHost = 'https://dotech-19fcd5663d3d.herokuapp.com';
    else if (isLocal) apiFromHost = 'http://localhost:8000';
    else apiFromHost = 'https://seojin-company-7364abe0dd19.herokuapp.com';

    // 회사명(임시): 환경/윈도에서 읽되, 마지막에 반드시 호스트로 강제 보정함
    const companyFromEnv =
        orUndef((viteEnv as any)?.VITE_COMPANY_NAME) ??
        orUndef(process.env.REACT_APP_COMPANY_NAME);
    const companyFromWindow = orUndef(fromWindow?.COMPANY_NAME);

    console.debug('[config][host]', { host, isDotech, isLocal });

    return {
        API_BASE_URL: apiFromWindow ?? apiFromVite ?? apiFromCRA ?? apiFromHost,
        COMPANY_NAME: companyFromEnv ?? companyFromWindow, // 임시
    };
}

// override(config.json)이면 최우선 적용 (있어도 마지막에 호스트로 보정)
function mergeConfig(base: Partial<AppConfig>, override?: Partial<AppConfig>): AppConfig {
    const merged: AppConfig = {
        API_BASE_URL:
            orUndef(override?.API_BASE_URL) ??
            orUndef(base.API_BASE_URL) ??
            '',

        COMPANY_NAME:
            orUndef(override?.COMPANY_NAME) ??
            orUndef(base.COMPANY_NAME) ??
            '(주)서진',
    };

    // 🔒 최종 강제 보정: 도메인 기준으로 회사명 확정 (무조건 반영)
    const { isDotech } = detectHost();
    merged.COMPANY_NAME = isDotech ? '디오테크' : '(주)서진';

    return merged;
}

let _config: AppConfig | null = null;
let _ready: Promise<AppConfig> | null = null;

export async function loadConfig(): Promise<AppConfig> {
    if (_ready) return _ready;

    _ready = (async () => {
        let fileConf: Partial<AppConfig> | undefined;

        // /config.json (옵션) – 있으면 읽되, 어차피 마지막에 호스트로 보정됨
        try {
            const res = await fetch('/config.json', { cache: 'no-store' });
            if (res.ok) fileConf = await res.json();
        } catch { /* ignore */ }

        const envConf = resolveEnv();
        _config = mergeConfig(envConf, fileConf);

        if (!_config.API_BASE_URL) {
            console.error('[config] API_BASE_URL 결정 실패. 도메인/환경변수 확인.');
        } else {
            console.log('[config] API_BASE_URL =', _config.API_BASE_URL);
        }
        console.log('[config] COMPANY_NAME =', _config.COMPANY_NAME);

        return _config;
    })();

    return _ready;
}

export const configReady: Promise<AppConfig> = (async () => _ready ?? loadConfig())();

export function getConfig(): AppConfig {
    if (_config) return _config;
    const base = resolveEnv();
    _config = mergeConfig(base);
    return _config;
}